'use client';

import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Loader2, Search, X } from 'lucide-react';

import { LinkProps, MegaNavSearchItem } from './MegaNav.types';
import { searchStyles } from './MegaNavSearch.styles';

export interface MegaNavSearchProps {
    className?: string;
    isMobile?: boolean;
    placeholder?: string;
    minChars?: number;
    debounceMs?: number;
    itemsLoader?: (query: string) => Promise<MegaNavSearchItem[]>;
    itemsEndpoint?: string;
    queryParam?: string;
    limitParam?: string;
    limit?: number;
    resultsPath?: string;
    onNavigate?: () => void;
    Link: ComponentType<LinkProps>;
}

const readItemsFromPayload = (
    payload: unknown,
    resultsPath?: string
): MegaNavSearchItem[] => {
    if (Array.isArray(payload)) {
        return payload as MegaNavSearchItem[];
    }

    if (payload && typeof payload === 'object') {
        if (resultsPath) {
            const nestedValue = resultsPath.split('.').reduce<unknown>((acc, key) => {
                if (!acc || typeof acc !== 'object') return undefined;
                return (acc as Record<string, unknown>)[key];
            }, payload);

            return Array.isArray(nestedValue) ? (nestedValue as MegaNavSearchItem[]) : [];
        }

        const defaultItems = (payload as Record<string, unknown>).items;
        return Array.isArray(defaultItems) ? (defaultItems as MegaNavSearchItem[]) : [];
    }

    return [];
};

const clampItems = (items: MegaNavSearchItem[], limit: number) => {
    if (!Array.isArray(items)) return [];
    return items.slice(0, Math.max(1, limit));
};

const MegaNavSearch = ({
                           className,
                           isMobile = false,
                           placeholder = 'Search',
                           minChars = 2,
                           debounceMs = 300,
                           itemsLoader,
                           itemsEndpoint,
                           queryParam = 'q',
                           limitParam = 'limit',
                           limit = 8,
                           resultsPath,
                           onNavigate,
                           Link,
                       }: MegaNavSearchProps) => {
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<MegaNavSearchItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isFocused, setIsFocused] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const requestIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const cacheRef = useRef<Record<string, MegaNavSearchItem[]>>({});
    const lastCompletedQueryRef = useRef<string>('');

    const trimmedQuery = useMemo(() => query.trim(), [query]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target as Node)) {
                setOpen(false);
                setHoveredIndex(-1);
                setActiveIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let timer: number | undefined;

        if (loading) {
            timer = window.setTimeout(() => {
                setShowLoading(true);
            }, 150);
        } else {
            setShowLoading(false);
        }

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loading]);

    useEffect(() => {
        if (trimmedQuery.length < minChars) {
            abortControllerRef.current?.abort();
            setItems([]);
            setLoading(false);
            setShowLoading(false);
            setOpen(false);
            setActiveIndex(-1);
            setHoveredIndex(-1);
            return;
        }

        if (!isFocused) {
            abortControllerRef.current?.abort();
            setLoading(false);
            setShowLoading(false);
            setOpen(false);
            setActiveIndex(-1);
            setHoveredIndex(-1);
            return;
        }

        setOpen(true);

        const cacheKey = [
            trimmedQuery,
            limit,
            resultsPath ?? '',
            itemsEndpoint ?? '',
            itemsLoader ? 'loader' : 'endpoint',
        ].join('::');

        if (cacheRef.current[cacheKey]) {
            setItems(cacheRef.current[cacheKey]);
            setLoading(false);
            setOpen(true);
            setActiveIndex(-1);
            setHoveredIndex(-1);
            lastCompletedQueryRef.current = trimmedQuery;
            return;
        }

        const currentRequestId = ++requestIdRef.current;

        const timer = window.setTimeout(async () => {
            abortControllerRef.current?.abort();

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            try {
                setLoading(true);

                let nextItems: MegaNavSearchItem[] = [];

                if (itemsLoader) {
                    nextItems = (await itemsLoader(trimmedQuery)) ?? [];
                } else if (itemsEndpoint) {
                    const url = new URL(itemsEndpoint, window.location.origin);
                    url.searchParams.set(queryParam, trimmedQuery);
                    url.searchParams.set(limitParam, String(limit));

                    const response = await fetch(url.toString(), {
                        method: 'GET',
                        headers: {
                            Accept: 'application/json',
                        },
                        signal: abortController.signal,
                    });

                    if (!response.ok) {
                        throw new Error(`Search request failed with ${response.status}`);
                    }

                    const payload = await response.json();
                    nextItems = readItemsFromPayload(payload, resultsPath);
                }

                if (requestIdRef.current !== currentRequestId) return;

                const normalizedItems = clampItems(nextItems, limit);
                cacheRef.current[cacheKey] = normalizedItems;
                lastCompletedQueryRef.current = trimmedQuery;

                setItems(normalizedItems);
                setOpen(true);
                setActiveIndex(-1);
                setHoveredIndex(-1);
            } catch (error) {
                const isAbortError =
                    error instanceof DOMException && error.name === 'AbortError';

                if (isAbortError) return;
                if (requestIdRef.current !== currentRequestId) return;

                setItems([]);
                setOpen(true);
                setActiveIndex(-1);
                setHoveredIndex(-1);
            } finally {
                if (requestIdRef.current === currentRequestId) {
                    setLoading(false);
                }
            }
        }, debounceMs);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        trimmedQuery,
        minChars,
        debounceMs,
        itemsLoader,
        itemsEndpoint,
        queryParam,
        limitParam,
        limit,
        resultsPath,
        isFocused,
    ]);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const resetSearch = () => {
        inputRef.current?.blur();
        setQuery('');
        setItems([]);
        setOpen(false);
        setLoading(false);
        setShowLoading(false);
        setActiveIndex(-1);
        setHoveredIndex(-1);
    };

    const clearSearch = () => {
        setQuery('');
        setItems([]);
        setOpen(false);
        setLoading(false);
        setShowLoading(false);
        setActiveIndex(-1);
        setHoveredIndex(-1);
        window.requestAnimationFrame(() => inputRef.current?.focus());
    };

    const closeSearch = () => {
        setOpen(false);
        setActiveIndex(-1);
        setHoveredIndex(-1);
    };

    const handleInputBlur = () => {
        setIsFocused(false);

        window.setTimeout(() => {
            const activeElement = document.activeElement;

            if (!activeElement || !containerRef.current?.contains(activeElement)) {
                closeSearch();
            }
        }, 120);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((prev) => {
                const nextIndex = Math.min(prev + 1, items.length - 1);
                return nextIndex < 0 ? 0 : nextIndex;
            });
            setHoveredIndex(-1);
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, 0));
            setHoveredIndex(-1);
        }

        if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
            setHoveredIndex(-1);
        }

        if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
            const nextHref = items[activeIndex].href;

            resetSearch();
            window.location.assign(nextHref);
        }
    };

    const inputStyle = {
        ...searchStyles.input,
        ...(isMobile ? searchStyles.inputMobile : null),
        ...(isFocused ? { border: '1px solid #6b7280' } : null),
    };

    const shouldShowTypeHint = trimmedQuery.length < minChars;
    const shouldShowEmpty = !loading && trimmedQuery.length >= minChars && items.length === 0;

    return (
        <div ref={containerRef} className={clsx(className)} style={searchStyles.root}>
            <Search size={16} style={searchStyles.leadingIcon} aria-hidden="true" />

            {showLoading ? (
                <Loader2 size={16} style={searchStyles.trailingIcon} aria-hidden="true" />
            ) : null}

            {!showLoading && query ? (
                <button
                    type="button"
                    aria-label="Clear search"
                    style={searchStyles.clearButton}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearSearch}
                >
                    <X size={15} strokeWidth={2.25} aria-hidden="true" />
                </button>
            ) : null}

            <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                    const nextValue = e.target.value;
                    const nextTrimmedValue = nextValue.trim();

                    setQuery(nextValue);
                    setActiveIndex(-1);
                    setHoveredIndex(-1);
                    setOpen(nextTrimmedValue.length >= minChars);
                }}
                onFocus={() => {
                    setIsFocused(true);
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                aria-label="Global search"
                placeholder={placeholder}
                style={inputStyle}
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="search"
            />

            {open && (
                <div
                    style={{
                        ...searchStyles.dropdown,
                        ...(isMobile ? searchStyles.dropdownMobile : searchStyles.dropdownDesktop),
                    }}
                >
                    <div style={searchStyles.resultsScroller}>
                        {shouldShowTypeHint ? (
                            <div style={searchStyles.emptyState}>Please type to perform search</div>
                        ) : shouldShowEmpty ? (
                            <div style={searchStyles.emptyState}>No results found</div>
                        ) : (
                            <ul style={searchStyles.list}>
                                {items.map((item, index) => {
                                    const isInteractive = hoveredIndex === index;
                                    const isActive = activeIndex === index;

                                    return (
                                        <li
                                            key={item.id}
                                            style={{
                                                ...searchStyles.listItem,
                                                ...(index === 0 ? searchStyles.firstListItem : null),
                                            }}
                                        >
                                            <Link
                                                href={item.href}
                                                onClick={() => {
                                                    resetSearch();
                                                    onNavigate?.();
                                                }}
                                                className={undefined}
                                            >
                                                <span
                                                    style={{
                                                        ...searchStyles.resultLinkBase,
                                                        ...searchStyles.resultLinkDefault,
                                                        ...(isInteractive ? searchStyles.resultLinkInteractive : null),
                                                        ...(isActive ? searchStyles.resultLinkActive : null),
                                                    }}
                                                    onMouseEnter={() => setHoveredIndex(index)}
                                                    onMouseLeave={() => setHoveredIndex(-1)}
                                                >
                                                    <span style={searchStyles.thumbWrap}>
                                                        {item.imageUrl ? (
                                                            <img
                                                                src={item.imageUrl}
                                                                alt={item.imageAlt ?? item.label}
                                                                style={searchStyles.thumbImage}
                                                            />
                                                        ) : null}
                                                    </span>

                                                    <span style={searchStyles.content}>
                                                        {item.categoryLabel || item.category ? (
                                                            <span style={searchStyles.category}>
                                                                {item.categoryLabel ?? item.category}
                                                            </span>
                                                        ) : null}
                                                        <span style={searchStyles.title}>{item.label}</span>
                                                        {item.description ? (
                                                            <span style={searchStyles.description}>{item.description}</span>
                                                        ) : null}
                                                    </span>
                                                </span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MegaNavSearch;
